import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInput } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { ViaCepService } from '../../../core/services/address/via-cep/via-cep.service';
import { EmployeeStatusService } from '../../../core/services/employee/employee-status/employee-status.service';
import { EmployeeService } from '../../../core/services/employee/employee/employee.service';
import { AddressTypeService } from '../../../core/services/address/address-type/address-type.service';
import { EmployeeRoleService } from '../../../core/services/employee/employee-role/employee-role.service';
import { LocationService } from '../../../core/services/location/location/location.service';
import {
  ErrorRequestModel,
  ResponseRequestModel,
} from '../../../core/models/response.model';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common'
import { estadosCidades } from '../../../core/models/addressIBGE'

export interface EstadoCidade {
  estado: string;
  UF: string;
  cidades: string[];
}

@Component({
  selector: 'modal-location-create',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButton,
    MatProgressSpinner,
    MatExpansionModule,
    MatFormFieldModule,
    MatCardModule,
    MatIcon,
    MatInput,
    MatDividerModule,
    MatAutocompleteModule,
  ],
  templateUrl: './modal-location-create.component.html',
  styleUrl: './modal-location-create.component.scss',
  providers: [
    ViaCepService,
    EmployeeStatusService,
    EmployeeService,
    EmployeeRoleService,
    AddressTypeService,
  ],
  standalone: true,
})
export class ModalLocationCreateComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private locationService: LocationService,
    private snackbar: SnackbarCustomService,
    private dialogRef: MatDialogRef<ModalLocationCreateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  @Output() responseCreate = new EventEmitter();

  form: FormGroup = new FormGroup({});
  loading: boolean = false;

  public estadosCidades: EstadoCidade[] = estadosCidades

  public estadosFiltrados$!: Observable<EstadoCidade[]>;
  public cidadesFiltradas$!: Observable<string[]>;
  public cidadesDoEstado: string[] = [];

  ngOnInit(): void {
    this.createForm();

    this.estadosFiltrados$ = this.form.get('state')!.valueChanges.pipe(
      startWith(''),
      map(value => {
        const nomeEstado = typeof value === 'string' ? value : value?.estado;
        return nomeEstado ? this._filterEstados(nomeEstado) : this.estadosCidades.slice();
      })
    );

    this.form.get('state')!.valueChanges.subscribe(value => {
      if (value && typeof value !== 'string') {
        this.form.get('city')?.enable();
        this.cidadesDoEstado = (value as EstadoCidade).cidades;
        this.form.get('city')?.setValue('');
      } else {
        this.form.get('city')?.disable();
        this.cidadesDoEstado = [];
        this.form.get('city')?.setValue('');
      }
    });

    // Lógica para filtrar as cidades
    this.cidadesFiltradas$ = this.form.get('city')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCidades(value || ''))
    );
  }

  close() {
    this.dialogRef.close();
  }

  createModel() {
    return {
      descricao: this.form.get('description')?.value,
      cidade: this.form.get('city')?.value,
      estado: (this.form.get('state')?.value as EstadoCidade).estado,
      pais: this.form.get('country')?.value,
    };
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      description: ['', [Validators.required, Validators.minLength(3)]],
      country: [{ value: 'Brasil', disabled: true }, [Validators.required]],
      state: ['', [Validators.required]],
      city: [{ value: '', disabled: true }, [Validators.required, Validators.minLength(3)]],
    });
  }

  createLocation() {
    this.loading = true;
    this.locationService.createLocation(this.createModel()).subscribe({
      next: (response: ResponseRequestModel) => {
        this.snackbar.success(response.message);
        this.responseCreate.emit(true);
        this.close();
      },
      error: (err: ErrorRequestModel) => {
        this.loading = false;
        this.snackbar.errorRequest(err);
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  private _filterEstados(value: string): EstadoCidade[] {
    const filterValue = value.toLowerCase();
    return this.estadosCidades.filter(
      estado => estado.estado.toLowerCase().includes(filterValue)
    );
  }

  private _filterCidades(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.cidadesDoEstado.filter(cidade =>
      cidade.toLowerCase().includes(filterValue)
    );
  }

  public displayEstado(estado: EstadoCidade | null): string {
    return estado ? estado.estado : '';
  }
}
